import { Button, Flex } from '@chakra-ui/react'
import { css } from '@emotion/react'
import React, { useCallback } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import type { Data, DebugType, Tape } from '@/client/query'

import { useToaster } from '@/components/Toast/useToaster'

const debugPageStyles = css`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  max-width: 80vw;
  width: 800px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  padding: 20px;
  z-index: 10;
  overflow-y: scroll;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  pre {
    white-space: pre-wrap;
  }
`
const debugButtonStyles = css`
  z-index: 10;
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${chakraTokenToVar('gray.solid')};
  padding: 5px 10px;
  border-radius: 10px;
  color: ${chakraTokenToVar('gray.contrast')};
  cursor: pointer;
  user-select: none;
  opacity: 0.8;
`
export default function QueryDebug({
  debug,
  tape,
  data,
}: {
  tape: Tape
  data: Data
  debug: DebugType | null
}): JSX.Element | null {
  const [show, setShow] = React.useState(false)
  const toast = useToaster()

  const copyToClipboard = useCallback(() => {
    navigator.clipboard
      .writeText(`${debug?.queries?.[0]}`)
      .then(() => {
        toast({
          title: 'You have successfully copied the query to your clipboard!',
          status: 'success',
        })
      })
      .catch(err => {
        toast({
          title: `Failed to copy query to clipboard: ${err.message}`,
          status: 'error',
        })
      })
  }, [toast, debug])

  if (debug == null) {
    return null
  }

  return (
    <>
      {show ? (
        <div css={debugPageStyles}>
          <h1>Debug View</h1>
          <Flex direction="row">
            <p>
              Page resolved with {debug.queries.length} queries in {debug.time}ms ({debug.timeQuery}{' '}
              spent waiting for data). Resulting data is {JSON.stringify(data).length} bytes.
            </p>
            <Button onClick={copyToClipboard}>Copy Query</Button>
          </Flex>
          {debug.queries.map(query => (
            <pre key={query}>{query}</pre>
          ))}
          <hr />
          <h3>Data</h3>
          <pre>{JSON.stringify(data)}</pre>
          <hr />
          <h3>Tape</h3>
          <pre>{JSON.stringify(tape)}</pre>
        </div>
      ) : null}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: debug-only component */}
      <div
        css={debugButtonStyles}
        onClick={() => {
          setShow(k => !k)
          if (!show) {
            // biome-ignore lint/suspicious/noConsole: debug tool intentionally logs to console
            console.log(data)
          }
        }}
      >
        {debug.time}
        <span className="unit">ms</span>
      </div>
    </>
  )
}
